import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import Layout from '../../components/Layout';
import disciplineService from '../../services/disciplineService';
import studentsService from '../../services/studentsService';
import DialogModal from '../../components/DialogModal';
import { useAuth } from '../../context/AuthContext';
import { MagnifyingGlass, Plus, Trash, CaretDown, CaretUp, MagnifyingGlassPlus, Check, X } from '@phosphor-icons/react';

const SEVERITIES = ['low', 'medium', 'high', 'critical'];
const ACTION_TYPES = [
  { value: 'warning', label: 'Warning' },
  { value: 'detention', label: 'Detention' },
  { value: 'suspension', label: 'Suspension' },
  { value: 'parent_meeting', label: 'Parent Meeting' },
  { value: 'other', label: 'Other' },
];

const emptyForm = {
  incident_date: new Date().toISOString().split('T')[0],
  category: '',
  description: '',
  severity: 'low',
  student_ids: [],
};

const emptyActionForm = {
  student_id: '',
  action_type: 'warning',
  action_date: new Date().toISOString().split('T')[0],
  details: '',
  demerit_points: 0,
};

function IncidentReportsPage() {
  const { user } = useAuth();
  const isAdmin = user && ['super_admin', 'school_admin'].includes(user.role);
  const [loading, setLoading] = useState(true);
  const [incidents, setIncidents] = useState([]);
  const [filteredIncidents, setFilteredIncidents] = useState([]);
  const [students, setStudents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [actionsByIncident, setActionsByIncident] = useState({});
  const [actionForm, setActionForm] = useState(emptyActionForm);
  const [recordingAction, setRecordingAction] = useState(false);
  const [tallyStudentId, setTallyStudentId] = useState('');
  const [tallyResult, setTallyResult] = useState(null);
  const [tallyLoading, setTallyLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    let filtered = incidents;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter((i) => i.category.toLowerCase().includes(term) || i.description.toLowerCase().includes(term));
    }
    if (statusFilter !== 'all') {
      filtered = filtered.filter((i) => i.status === statusFilter);
    }
    setFilteredIncidents(filtered);
  }, [incidents, searchTerm, statusFilter]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [incidentsData, studentsData] = await Promise.all([
        disciplineService.listIncidents(),
        studentsService.listStudents().catch(() => []),
      ]);
      setIncidents(Array.isArray(incidentsData) ? incidentsData : []);
      setStudents(Array.isArray(studentsData) ? studentsData : studentsData?.items || studentsData?.students || []);
    } catch (error) {
      toast.error('Failed to load incidents');
    } finally {
      setLoading(false);
    }
  };

  const getStudentName = (studentId) => {
    const student = students.find((s) => s.student_id === studentId);
    return student ? `${student.first_name} ${student.last_name}` : studentId;
  };

  const openCreateModal = () => {
    setFormData(emptyForm);
    setShowModal(true);
  };

  const toggleStudent = (studentId) => {
    setFormData((prev) => ({
      ...prev,
      student_ids: prev.student_ids.includes(studentId)
        ? prev.student_ids.filter((id) => id !== studentId)
        : [...prev.student_ids, studentId],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.category || !formData.description || formData.student_ids.length === 0) {
      toast.error('Category, description, and at least one student are required');
      return;
    }
    setSubmitting(true);
    try {
      await disciplineService.createIncident(formData);
      toast.success('Incident reported');
      setShowModal(false);
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to report incident');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusChange = async (incident, newStatus) => {
    try {
      await disciplineService.updateIncident(incident.id, { status: newStatus });
      toast.success('Status updated');
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to update status');
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await disciplineService.deleteIncident(deleteTarget);
      toast.success('Incident deleted');
      setDeleteTarget(null);
      loadData();
    } catch (error) {
      toast.error('Failed to delete incident');
    } finally {
      setDeleteLoading(false);
    }
  };

  const toggleExpand = async (incident) => {
    if (expandedId === incident.id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(incident.id);
    setActionForm({ ...emptyActionForm, student_id: incident.students[0]?.student_id || '' });
    try {
      const actions = await disciplineService.listActions(incident.id);
      setActionsByIncident((prev) => ({ ...prev, [incident.id]: Array.isArray(actions) ? actions : [] }));
    } catch (error) {
      toast.error('Failed to load actions');
    }
  };

  const handleRecordAction = async (incidentId) => {
    if (!actionForm.student_id || !actionForm.action_type || !actionForm.action_date) {
      toast.error('Please fill in the required action fields');
      return;
    }
    setRecordingAction(true);
    try {
      await disciplineService.createAction(incidentId, {
        ...actionForm,
        demerit_points: Number(actionForm.demerit_points) || 0,
      });
      toast.success('Action recorded');
      const actions = await disciplineService.listActions(incidentId);
      setActionsByIncident((prev) => ({ ...prev, [incidentId]: Array.isArray(actions) ? actions : [] }));
      setActionForm((prev) => ({ ...emptyActionForm, student_id: prev.student_id }));
    } catch (error) {
      toast.error(error.message || 'Failed to record action');
    } finally {
      setRecordingAction(false);
    }
  };

  const refreshActions = async (incidentId) => {
    const actions = await disciplineService.listActions(incidentId);
    setActionsByIncident((prev) => ({ ...prev, [incidentId]: Array.isArray(actions) ? actions : [] }));
  };

  const handleApproveAction = async (incidentId, actionId) => {
    try {
      await disciplineService.approveAction(actionId);
      toast.success('Action approved');
      refreshActions(incidentId);
    } catch (error) {
      toast.error(error.message || 'Failed to approve action');
    }
  };

  const handleRejectAction = async (incidentId, actionId) => {
    try {
      await disciplineService.rejectAction(actionId);
      toast.success('Action rejected');
      refreshActions(incidentId);
    } catch (error) {
      toast.error(error.message || 'Failed to reject action');
    }
  };

  const handleTallyLookup = async () => {
    if (!tallyStudentId) return;
    setTallyLoading(true);
    try {
      const result = await disciplineService.getStudentTally(tallyStudentId);
      setTallyResult(result);
    } catch (error) {
      toast.error('Failed to look up demerit tally');
    } finally {
      setTallyLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="loading">Loading incidents...</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page">
        <div className="page-header">
          <div>
            <h1>Discipline — Incident Reports</h1>
            <p>Report incidents, record actions taken, and track demerit tallies</p>
          </div>
          <button className="btn btn-primary" onClick={openCreateModal}>
            <Plus size={20} />
            Report Incident
          </button>
        </div>

        <div className="form-card" style={{ marginBottom: 20, padding: 16 }}>
          <h3 style={{ marginTop: 0 }}>Demerit Tally Lookup</h3>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <select value={tallyStudentId} onChange={(e) => { setTallyStudentId(e.target.value); setTallyResult(null); }} className="filter-select" style={{ minWidth: 220 }}>
              <option value="">Select Student</option>
              {students.map((s) => (
                <option key={s.id} value={s.student_id}>{s.first_name} {s.last_name}</option>
              ))}
            </select>
            <button className="btn btn-secondary" onClick={handleTallyLookup} disabled={!tallyStudentId || tallyLoading}>
              <MagnifyingGlassPlus size={18} />
              {tallyLoading ? 'Looking up...' : 'Look Up'}
            </button>
            {tallyResult && (
              <span style={{ fontWeight: 600 }}>
                Total demerit points: {tallyResult.total_demerit_points}
                {tallyResult.at_suspension_threshold && (
                  <span style={{ color: '#dc2626', marginLeft: 6 }}>(at/above suspension threshold)</span>
                )}
              </span>
            )}
          </div>
        </div>

        <div className="filters-bar">
          <div className="search-input">
            <MagnifyingGlass size={20} />
            <input
              type="text"
              placeholder="Search by category or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="filter-select">
            <option value="all">All Statuses</option>
            <option value="open">Open</option>
            <option value="resolved">Resolved</option>
          </select>
        </div>

        <div className="table-container">
          {filteredIncidents.length === 0 ? (
            <div className="empty-state">
              <p>No incidents found</p>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th></th>
                  <th>Date</th>
                  <th>Category</th>
                  <th>Severity</th>
                  <th>Students</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredIncidents.map((incident) => (
                  <React.Fragment key={incident.id}>
                    <tr>
                      <td>
                        <button className="btn-icon" onClick={() => toggleExpand(incident)} title="Expand">
                          {expandedId === incident.id ? <CaretUp size={16} /> : <CaretDown size={16} />}
                        </button>
                      </td>
                      <td>{incident.incident_date}</td>
                      <td>{incident.category}</td>
                      <td className="text-capitalize">{incident.severity}</td>
                      <td>{incident.students.map((s) => getStudentName(s.student_id)).join(', ')}</td>
                      <td>
                        <select
                          value={incident.status}
                          onChange={(e) => handleStatusChange(incident, e.target.value)}
                          className="filter-select"
                        >
                          <option value="open">Open</option>
                          <option value="resolved">Resolved</option>
                        </select>
                      </td>
                      <td className="action-buttons">
                        <button className="btn-icon btn-danger" title="Delete" onClick={() => setDeleteTarget(incident.id)}>
                          <Trash size={18} />
                        </button>
                      </td>
                    </tr>
                    {expandedId === incident.id && (
                      <tr>
                        <td colSpan={7} style={{ background: 'var(--muted, #f8f9fa)', padding: 16 }}>
                          <p style={{ margin: '0 0 12px' }}>{incident.description}</p>
                          <h4 style={{ margin: '0 0 8px' }}>Actions Taken</h4>
                          {(actionsByIncident[incident.id] || []).length === 0 ? (
                            <p style={{ color: '#666' }}>No actions recorded yet.</p>
                          ) : (
                            <ul style={{ marginBottom: 16 }}>
                              {(actionsByIncident[incident.id] || []).map((a) => (
                                <li key={a.id}>
                                  {getStudentName(a.student_id)} — {ACTION_TYPES.find((t) => t.value === a.action_type)?.label || a.action_type} on {a.action_date}
                                  {a.demerit_points ? ` (${a.demerit_points} demerit pts)` : ''}
                                  {a.details ? `: ${a.details}` : ''}
                                  {a.approval_status === 'pending' && (
                                    <span style={{ marginLeft: 8, color: '#b45309', fontWeight: 600 }}>
                                      (pending approval)
                                      {isAdmin && (
                                        <>
                                          {' '}
                                          <button
                                            className="btn-icon"
                                            title="Approve"
                                            onClick={() => handleApproveAction(incident.id, a.id)}
                                            style={{ color: '#16a34a' }}
                                          >
                                            <Check size={14} />
                                          </button>
                                          <button
                                            className="btn-icon"
                                            title="Reject"
                                            onClick={() => handleRejectAction(incident.id, a.id)}
                                            style={{ color: '#dc2626' }}
                                          >
                                            <X size={14} />
                                          </button>
                                        </>
                                      )}
                                    </span>
                                  )}
                                  {a.approval_status === 'rejected' && (
                                    <span style={{ marginLeft: 8, color: '#dc2626', fontWeight: 600 }}>(rejected)</span>
                                  )}
                                </li>
                              ))}
                            </ul>
                          )}
                          <h4 style={{ margin: '0 0 8px' }}>Record New Action</h4>
                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                            <div className="form-group" style={{ margin: 0 }}>
                              <label>Student</label>
                              <select value={actionForm.student_id} onChange={(e) => setActionForm({ ...actionForm, student_id: e.target.value })}>
                                {incident.students.map((s) => (
                                  <option key={s.student_id} value={s.student_id}>{getStudentName(s.student_id)}</option>
                                ))}
                              </select>
                            </div>
                            <div className="form-group" style={{ margin: 0 }}>
                              <label>Action Type</label>
                              <select value={actionForm.action_type} onChange={(e) => setActionForm({ ...actionForm, action_type: e.target.value })}>
                                {ACTION_TYPES.map((t) => (
                                  <option key={t.value} value={t.value}>{t.label}</option>
                                ))}
                              </select>
                            </div>
                            <div className="form-group" style={{ margin: 0 }}>
                              <label>Date</label>
                              <input type="date" value={actionForm.action_date} onChange={(e) => setActionForm({ ...actionForm, action_date: e.target.value })} />
                            </div>
                            <div className="form-group" style={{ margin: 0 }}>
                              <label>Demerit Points</label>
                              <input type="number" min="0" value={actionForm.demerit_points} onChange={(e) => setActionForm({ ...actionForm, demerit_points: e.target.value })} style={{ width: 90 }} />
                            </div>
                            <div className="form-group" style={{ margin: 0, flex: 1, minWidth: 160 }}>
                              <label>Details</label>
                              <input type="text" value={actionForm.details} onChange={(e) => setActionForm({ ...actionForm, details: e.target.value })} />
                            </div>
                            <button className="btn btn-primary" onClick={() => handleRecordAction(incident.id)} disabled={recordingAction}>
                              {recordingAction ? 'Saving...' : 'Record Action'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Report Incident</h2>
              <button type="button" className="btn-close" onClick={() => setShowModal(false)} aria-label="Close">×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>Date *</label>
                  <input type="date" value={formData.incident_date} onChange={(e) => setFormData({ ...formData, incident_date: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label>Severity *</label>
                  <select value={formData.severity} onChange={(e) => setFormData({ ...formData, severity: e.target.value })} required>
                    {SEVERITIES.map((s) => (
                      <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Category *</label>
                <input type="text" value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })} placeholder="e.g., Bullying, Fighting" required />
              </div>

              <div className="form-group">
                <label>Description *</label>
                <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} required />
              </div>

              <div className="form-group">
                <label>Students Involved * ({formData.student_ids.length} selected)</label>
                <div style={{ maxHeight: 160, overflowY: 'auto', border: '1px solid #ddd', borderRadius: 8, padding: 8 }}>
                  {students.map((s) => (
                    <label key={s.id} style={{ display: 'block', padding: '4px 0' }}>
                      <input
                        type="checkbox"
                        checked={formData.student_ids.includes(s.student_id)}
                        onChange={() => toggleStudent(s.student_id)}
                        style={{ width: 'auto', marginRight: 8 }}
                      />
                      {s.first_name} {s.last_name}
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-actions">
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? 'Saving...' : 'Report Incident'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <DialogModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Delete Incident"
        message="This will permanently delete this incident and its recorded actions. Continue?"
        type="warning"
        isLoading={deleteLoading}
        primaryAction={{ label: 'Delete', onClick: confirmDelete, loadingLabel: 'Deleting...' }}
        secondaryAction={{ label: 'Cancel' }}
      />
    </Layout>
  );
}

export default IncidentReportsPage;
